import { Box, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { Button } from 'utoniq-ui'

import { Maybe } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { ItemProps, ListItemImage } from '@/components/organisms'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  fetching: boolean
  hasNextPage: boolean
  customProfileUpdating: boolean
  items: ItemProps[]
  default: Maybe<ItemProps>
}

type CustomProfileEditCellProps = {
  thumbnail: Maybe<ItemProps['thumbnail']>
}

export type Action = {
  onSaveCustomProfile: (wallpaperItemId: Maybe<string>) => Promise<void>
  onFetchMore: () => Promise<void>
}

export const CustomProfileEditWallPaperTemplate: Template<State, Action> = ({
  state,
  onSaveCustomProfile,
  onFetchMore,
}) => {
  const { t } = useTranslation('customProfileEditWallPaper')
  const { showError } = useMessage()
  const { routerNames, routerPages } = useRouter()
  const [selected, setSelected] = useState<Maybe<ItemProps>>(null)

  useEffect(() => {
    if (state.default) {
      setSelected(state.default)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.default?.id])

  const handleSaveCustomProfile = () => {
    onSaveCustomProfile(selected?.id || null).catch(showError)
  }

  return (
    <BaseLayout
      header={{
        back: true,
        defaultBackPage: routerPages.MyProfile,
        title: t('title'),
        right: (
          <Button
            size="small"
            onClick={handleSaveCustomProfile}
            disabled={
              state.customProfileUpdating || state.default?.id === selected?.id
            }
            loading={state.customProfileUpdating}
            sx={{ mr: -1 }}
          >
            {t('save')}
          </Button>
        ),
      }}
    >
      <Head title={routerNames.CustomProfileEditWallPaper} />
      {!state.loading && (
        <>
          <CustomProfileEditCell thumbnail={selected?.thumbnail ?? null} />
          <ListItemImage
            items={state.items}
            defaultValue={state.default?.id ?? ''}
            selected={selected}
            onSelected={setSelected}
            loading={state.fetching}
            hasNextPage={state.hasNextPage}
            onFetchMore={onFetchMore}
            sx={{ minHeight: 192 }}
          />
        </>
      )}
    </BaseLayout>
  )
}

const CustomProfileEditCell: Cell<CustomProfileEditCellProps> = ({
  thumbnail,
}) => {
  const { t } = useTranslation('customProfileEditWallPaper')
  if (!thumbnail?.uri) {
    return (
      <CellFrame>
        <Stack
          sx={{ width: '100%', height: 400, bgcolor: 'grey.50' }}
          alignItems="center"
          justifyContent="center"
        >
          <Stack spacing={3}>
            <Typography variant="h5" align="center">
              {t('wallpaperNotSetTitle')}
            </Typography>
            <Typography variant="body1" align="center">
              {t('wallpaperNotSetDesc')}
            </Typography>
          </Stack>
        </Stack>
      </CellFrame>
    )
  }

  return (
    <CellFrame>
      <Box
        sx={{
          width: '100%',
          height: 400,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundImage: `url('${thumbnail.uri}')`,
        }}
      />
    </CellFrame>
  )
}
